import type { BadgeTone } from '../../components/Badge';
import type { TaskBoard, TaskCard, TaskPriority, TaskStatus } from '../../api/tasks';

/**
 * Las columnas del kanban (`13 §3.6`).
 *
 * La API responde una columna por estado de tarea —quince— y quince columnas
 * son un tablero que nadie puede recorrer con la vista. El boceto de `13 §3.6`
 * dibuja cinco; estas siete son esas cinco más los dos estados que no pueden
 * esconderse detrás del camino feliz: `blocked` (una tarea esperando a una
 * persona) y los fallos terminales, que es donde vive el botón de reintentar.
 * Cada tarjeta lleva su propia etiqueta de estado, así que una columna agrupada
 * sigue siendo honesta sobre en qué estado está cada tarjeta.
 *
 * La etiqueta ya no vive aquí: es una clave del catálogo, `tasks.columns.*`,
 * porque `key` es un identificador y la etiqueta es prosa.
 */
export const BOARD_COLUMNS: { key: string; statuses: TaskStatus[] }[] = [
  { key: 'queue', statuses: ['draft', 'queued', 'triaged', 'assigned'] },
  { key: 'working', statuses: ['meeting', 'planning', 'in_progress'] },
  { key: 'review', statuses: ['in_review', 'qa'] },
  // `awaiting_deploy` (2026-09-08): mergeada y esperando a Jenkins, la misma
  // cola de «ya está el código, falta que llegue» que la PR y la CI.
  { key: 'pr', statuses: ['pr_open', 'awaiting_ci', 'awaiting_deploy'] },
  { key: 'approval', statuses: ['awaiting_approval'] },
  { key: 'blocked', statuses: ['blocked'] },
  { key: 'finished', statuses: ['done', 'failed', 'cancelled'] },
];

/**
 * Los tres estados de los que no se sale solo (`09 §1`) — la columna
 * «Terminadas»— y por eso los únicos que la API deja archivar. La misma lista
 * que `TaskStatus::isTerminal()` en el servidor, que es quien decide de verdad.
 */
export const TERMINAL_STATUSES: Set<TaskStatus> = new Set(
  BOARD_COLUMNS.find((column) => column.key === 'finished')?.statuses ?? [],
);

/**
 * Las tarjetas de una columna agrupada, movimiento más reciente primero: el
 * mismo orden en que ya llega cada columna de la API, conservado en la mezcla.
 */
export function columnCards(board: TaskBoard, statuses: TaskStatus[]): TaskCard[] {
  return statuses
    .flatMap((status) => board.columns[status] ?? [])
    .sort((a, b) => (a.updated_at < b.updated_at ? 1 : -1));
}

export function columnCount(board: TaskBoard, statuses: TaskStatus[]): number {
  return statuses.reduce((total, status) => total + (board.counts[status] ?? 0), 0);
}

/**
 * El tono de la etiqueta de estado.
 *
 * Devuelve un tono del sistema y no clases sueltas: la etiqueta la pinta
 * `<Badge>`, que además garantiza que el estado lleve siempre su palabra —el
 * color nunca es el único canal (`13 §7`)—.
 */
const STATUS_TONE: Partial<Record<TaskStatus, BadgeTone>> = {
  blocked: 'warning',
  awaiting_approval: 'warning',
  failed: 'danger',
  cancelled: 'danger',
  done: 'success',
  in_progress: 'primary',
  pr_open: 'info',
  awaiting_ci: 'info',
  awaiting_deploy: 'info',
};

export function statusTone(status: TaskStatus): BadgeTone {
  return STATUS_TONE[status] ?? 'neutral';
}

/**
 * La marca de prioridad.
 *
 * Eran emoji de círculos de colores, que en `low` y en `normal` eran además el
 * mismo carácter: dos prioridades distintas con el mismo dibujo. Ahora es una
 * barra con altura y tono, que se distingue de un vistazo y no depende de la
 * fuente de emoji del sistema.
 */
export const PRIORITY_TONE: Record<TaskPriority, BadgeTone> = {
  low: 'neutral',
  normal: 'neutral',
  high: 'info',
  urgent: 'danger',
};

export const PRIORITY_BARS: Record<TaskPriority, number> = {
  low: 1,
  normal: 2,
  high: 3,
  urgent: 4,
};

/**
 * Las pestañas del tablero (`13 §3.6`, desde el 2026-09-11): un tablero por
 * proyecto, no uno para todos.
 *
 * `NO_PROJECT` es la pestaña de las tareas sin proyecto y también el valor que
 * viaja a la API como `?project_id=none`: la misma palabra en la URL, en la
 * petición y en el estado, para que no haya que traducir por el camino.
 */
export const NO_PROJECT = 'none';

export interface BoardTab {
  /** Lo que va en `?project=`: el slug del proyecto, o `NO_PROJECT`. */
  key: string;
  /** Lo que viaja a la API como `project_id`. */
  projectId: string;
  name: string;
}

/**
 * Las pestañas, por nombre y no por fecha de creación.
 *
 * La lista de proyectos llega de más nuevo a más viejo, que es lo que quiere
 * la pantalla de proyectos; una fila de pestañas se recorre con la vista y
 * un orden alfabético es el único que se puede predecir sin mirar.
 */
export function boardTabs(
  projects: { id: string; slug: string; name: string }[],
  unassignedName: string,
  hasUnassigned: boolean,
): BoardTab[] {
  const tabs = [...projects]
    .sort((a, b) => a.name.localeCompare(b.name))
    .map((project) => ({ key: project.slug, projectId: project.id, name: project.name }));

  if (hasUnassigned) {
    tabs.push({ key: NO_PROJECT, projectId: NO_PROJECT, name: unassignedName });
  }

  return tabs;
}

/**
 * Qué pestaña enseñar: la de la URL si existe, si no la última que se miró
 * en esta empresa, si no la primera. `null` es «no hay nada que enseñar»:
 * ni proyectos ni tareas sueltas.
 *
 * La URL manda sobre la memoria a propósito —un enlace a `?project=web-shop`
 * tiene que abrir Web Shop aunque ayer se mirara otro— y una URL que nombra
 * un proyecto que ya no está (archivado, borrado, de otra empresa) cae al
 * siguiente escalón en vez de a una pestaña vacía que no existe.
 */
export function resolveTab(tabs: BoardTab[], requested: string | undefined, remembered: string | null): BoardTab | null {
  const has = (key: string | null | undefined) => tabs.find((tab) => tab.key === key);

  return has(requested) ?? has(remembered) ?? tabs[0] ?? null;
}
